<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <div v-if="!showDetailedView" class="space-y-8">
          
          <!-- ✅ 1. Summary Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div @click="showDetailedView = true" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-blue-200 dark:hover:border-blue-700 transition-all">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Records</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ totalRecords }}</p>
                </div>
                <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01m-.01 4h.01" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">All attendance records this month</p>
            </div>

            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Present Today</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ presentToday }}</p>
                </div>
                <div class="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Employees present today</p>
            </div>

            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Absent Today</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ absentToday }}</p>
                </div>
                <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Employees absent today</p>
            </div>

            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Late Today</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ lateToday }}</p>
                </div>
                <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Employees who arrived late</p>
            </div>
          </div>

          <!-- ✅ 2. Header with Search & Actions -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Attendance Management</h2>
            <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div class="relative w-full sm:w-72">
                <input v-model="employeeSearch" @input="searchEmployees" type="text" placeholder="Search by Staff ID or Name" class="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
                <svg class="absolute left-3 top-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <ul v-if="searchResults.length > 0 && employeeSearch" class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                  <li v-for="employee in searchResults" :key="employee.id" @click="selectEmployee(employee)" class="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <div class="flex justify-between">
                      <span class="font-medium text-gray-900 dark:text-white">{{ employee.name }}</span>
                      <span class="text-gray-500 dark:text-gray-400 text-xs">{{ employee.position }}</span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">ID: {{ employee.id }}</div>
                  </li>
                </ul>
              </div>

              <button @click="exportToExcel" class="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Excel
              </button>
              <button @click="openNewAttendanceModal" class="inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#65a348] transition-colors">
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                New Attendance
              </button>
              <button @click="openBatchModal" class="inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#035a76] hover:bg-[#024c63] transition-colors">
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Batch Attendance
              </button>
            </div>
          </div>


          
          <!-- ✅ 5. Selected Employee Card -->
          <div v-if="selectedEmployee" class="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Selected Employee</h3>
              <button @click="clearSelectedEmployee" class="text-gray-400 hover:text-red-500 text-2xl font-bold transition-colors">×</button>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Employee Name</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployee.name }}</p>
              </div>
              <div class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Position</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployee.position }}</p>
              </div>
              <div class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Department</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployee.department }}</p>
              </div>
              <div class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Staff ID</p>
                <p class="mt-1 font-mono font-medium text-gray-900 dark:text-white">{{ selectedEmployee.id }}</p>
              </div>
            </div>
          </div>

                <!-- ✅ 8. Attendance Summary for Selected Employee -->
          <div v-if="selectedEmployee" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Attendance Summary</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <p class="text-sm font-medium text-green-800 dark:text-green-300">Days Present</p>
                <p class="text-3xl font-bold text-green-900 dark:text-green-200 mt-1">{{ attendanceSummary.present }}</p>
                <p class="mt-2 text-xs text-green-700 dark:text-green-400">This month</p>
              </div>
              <div class="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                <p class="text-sm font-medium text-red-800 dark:text-red-300">Days Absent</p>
                <p class="text-3xl font-bold text-red-900 dark:text-red-200 mt-1">{{ attendanceSummary.absent }}</p>
                <p class="mt-2 text-xs text-red-700 dark:text-red-400">This month</p>
              </div>
              <div class="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p class="text-sm font-medium text-yellow-800 dark:text-yellow-300">Late Arrivals</p>
                <p class="text-3xl font-bold text-yellow-900 dark:text-yellow-200 mt-1">{{ attendanceSummary.late }}</p>
                <p class="mt-2 text-xs text-yellow-700 dark:text-yellow-400">This month</p>
              </div>
            </div>
          </div>

          <!-- ✅ 6. Employee-Specific Filters -->
          <div v-if="selectedEmployee" class="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 class="text-md font-semibold text-gray-900 dark:text-white mb-4">Filter {{ selectedEmployee.name }}'s Attendance</h3>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">From Date</label>
                <input v-model="filterFromDate" type="date" @change="fetchAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">To Date</label>
                <input v-model="filterToDate" type="date" @change="fetchAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Status</label>
                <select v-model="filterStatus" @change="fetchAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
                  <option value="all">All</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                </select>
              </div>
              <div class="flex items-end">
                <button @click="resetFilters" class="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg transition font-medium">Reset</button>
              </div>
            </div>
          </div>

          <!-- ✅ 7. Attendance History for Selected Employee -->
          <div v-if="selectedEmployee" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Attendance History</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">Records for {{ selectedEmployee.name }}</p>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Check-In</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Check-Out</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</th>
                    <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="record in attendanceRecords" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(record.date) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="getStatusClass(record.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                        <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="getStatusDot(record.status)"></span>
                        {{ record.status }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.checkIn || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.checkOut || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ record.notes || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button @click="viewAttendanceDetails(record)" class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3 transition-colors">View</button>
                      <button @click="correctRecord(record)" class="text-[#76ba51] hover:text-[#65a348] transition-colors">Correct</button>
                    </td>
                  </tr>
                  <tr v-if="attendanceRecords.length === 0">
                    <td colspan="6" class="px-6 py-12 text-center">
                      <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No attendance records found</h3>
                      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">No records yet for {{ selectedEmployee.name }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

    
          <!-- ✅ 3. Daily Attendance Filters -->
          <div class="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">From Date</label>
                <input v-model="dailyFilterFromDate" type="date" @change="fetchDailyAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">To Date</label>
                <input v-model="dailyFilterToDate" type="date" @change="fetchDailyAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Status</label>
                <select v-model="dailyFilterStatus" @change="fetchDailyAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
                  <option value="all">All Statuses</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Department</label>
                <select v-model="dailyFilterDepartment" @change="fetchDailyAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
                  <option value="all">All Departments</option>
                  <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Position</label>
                <select v-model="dailyFilterPosition" @change="fetchDailyAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
                  <option value="all">All Positions</option>
                  <option v-for="pos in positions" :key="pos" :value="pos">{{ pos }}</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Class</label>
                <select v-model="dailyFilterClass" @change="fetchDailyAttendance" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent transition">
                  <option value="all">All Classes</option>
                  <option v-for="cls in classes" :key="cls" :value="cls">{{ cls }}</option>
                </select>
              </div>
              <div class="flex items-end">
                <button @click="resetDailyFilters" class="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg transition font-medium">Reset Filters</button>
              </div>
            </div>
          </div>
          <!-- ✅ 4. Daily Attendance Table -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Daily Attendance Records</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ filteredDailyAttendance.length }} records found</p>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff ID</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Department</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Class</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Check-In</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Check-Out</th>
                    <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="record in filteredDailyAttendance" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {{ getInitials(record.employeeName) }}
                        </div>
                        <div class="ml-3">
                          <div class="text-sm font-medium text-gray-900 dark:text-white">{{ record.employeeName }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.employeeId }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.department }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.position }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.class || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(record.date) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="getStatusClass(record.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                        <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="getStatusDot(record.status)"></span>
                        {{ record.status }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.checkIn || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ record.checkOut || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button @click="viewAttendanceDetails(record)" class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3 transition-colors">View</button>
                      <button v-if="isManager" @click="correctRecord(record)" class="text-[#76ba51] hover:text-[#65a348] transition-colors">Edit</button>
                    </td>
                  </tr>
                  <tr v-if="filteredDailyAttendance.length === 0">
                    <td colspan="10" class="px-6 py-12 text-center">
                      <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No attendance records found</h3>
                      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>


        </div>

        <!-- ========================================== -->
        <!-- ✅ FULLY RESTORED MODALS -->
        <!-- ========================================== -->

        <!-- 1. New Attendance Modal -->
        <div v-if="showNewAttendanceModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">New Attendance Record</h3>
              <button @click="showNewAttendanceModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div class="px-6 py-6 space-y-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                <input v-model="newAttendance.date" type="date" required class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#76ba51] focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select v-model="newAttendance.status" @change="updateStatusId" class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#76ba51] focus:border-transparent">
                  <option v-for="status in statuses" :key="status.id" :value="status.name">{{ status.name }}</option>
                </select>
              </div>
              <div v-if="requiresCheckIn(newAttendance.status)">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Check-In Time</label>
                <input v-model="newAttendance.checkIn" type="time" :required="requiresCheckIn(newAttendance.status)" class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#76ba51] focus:border-transparent">
              </div>
              <div v-if="requiresCheckIn(newAttendance.status)">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Check-Out Time</label>
                <input v-model="newAttendance.checkOut" type="time" class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#76ba51] focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea v-model="newAttendance.notes" rows="3" class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#76ba51] focus:border-transparent resize-none" placeholder="Enter any notes..."></textarea>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <button @click="showNewAttendanceModal = false" type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">Cancel</button>
              <button @click="submitAttendance" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#65a348] transition-colors">Submit Record</button>
            </div>
          </div>
        </div>

        <!-- 2. Batch Attendance Modal -->
        <div v-if="showBatchModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl my-8 border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-[#035a76] rounded-t-xl">
              <div>
                <h3 class="text-lg font-semibold text-white">Batch Attendance</h3>
                <p class="text-xs text-blue-200 mt-0.5">Select a department, tick who's present — everyone else is marked absent</p>
              </div>
              <button @click="closeBatchModal" class="text-white hover:text-red-300 text-2xl font-bold leading-none">×</button>
            </div>
            
            <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department <span class="text-red-500">*</span></label>
                  <select v-model="batchDepartmentCode" @change="loadBatchStaff" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent">
                    <option value="">— Select Department —</option>
                    <option v-for="dept in departmentObjects" :key="dept.code" :value="dept.code">{{ dept.name }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date <span class="text-red-500">*</span></label>
                  <input v-model="batchDate" type="date" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent" />
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Check-In</label>
                    <input v-model="batchDefaultCheckIn" type="time" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Check-Out</label>
                    <input v-model="batchDefaultCheckOut" type="time" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent" />
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="batchStaff.length > 0" class="px-6 py-4">
              <div class="flex flex-wrap justify-between items-center mb-3 gap-3">
                <div class="flex items-center gap-4">
                  <label class="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-gray-700 dark:text-gray-300">
                    <input type="checkbox" :checked="allBatchChecked" :indeterminate.prop="someBatchChecked" @change="toggleAllBatch" class="w-4 h-4 rounded border-gray-300 text-[#76ba51] focus:ring-[#76ba51]" />
                    Select All
                  </label>
                  <span class="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded-full font-medium">✓ {{ batchPresentCount }} Present</span>
                  <span class="text-xs bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-2 py-1 rounded-full font-medium">✗ {{ batchStaff.length - batchPresentCount }} Absent</span>
                </div>
                <input v-model="batchStaffSearch" type="text" placeholder="Filter staff…" class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-56 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#76ba51] focus:border-transparent" />
              </div>
              
              <div class="overflow-y-auto max-h-96 rounded-lg border border-gray-200 dark:border-gray-700">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead class="bg-gray-50 dark:bg-gray-800/50 sticky top-0">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-10">Present</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Staff ID</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Position</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Check-In</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr v-for="staff in filteredBatchStaff" :key="staff.applicant_id" :class="staff.isPresent ? 'bg-green-50 dark:bg-green-900/10' : 'bg-white dark:bg-gray-800'" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td class="px-4 py-3">
                        <input type="checkbox" v-model="staff.isPresent" @change="onBatchCheckChange(staff)" class="w-4 h-4 rounded border-gray-300 text-[#76ba51] focus:ring-[#76ba51]" />
                      </td>
                      <td class="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{{ staff.name }}</td>
                      <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{{ staff.id }}</td>
                      <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{{ staff.position }}</td>
                      <td class="px-4 py-3">
                        <span v-if="staff.isPresent">
                          <select v-model="staff.statusId" class="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-[#76ba51] focus:border-[#76ba51]">
                            <option v-for="s in presentStatuses" :key="s.id" :value="s.id">{{ s.name }}</option>
                          </select>
                        </span>
                        <span v-else class="px-2 py-1 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">Absent</span>
                      </td>
                      <td class="px-4 py-3">
                        <input v-if="staff.isPresent" v-model="staff.checkIn" type="time" class="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-28 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-[#76ba51] focus:border-[#76ba51]" />
                        <span v-else class="text-xs text-gray-400">—</span>
                      </td>
                    </tr>
                    <tr v-if="filteredBatchStaff.length === 0">
                      <td colspan="6" class="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No staff found</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div v-else-if="batchDepartmentCode && !batchLoading" class="px-6 py-10 text-center text-gray-500 dark:text-gray-400 text-sm">No staff found for the selected department.</div>
            <div v-else-if="batchLoading" class="px-6 py-10 text-center text-gray-500 dark:text-gray-400 text-sm flex items-center justify-center gap-2">
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Loading staff…
            </div>
            <div v-else class="px-6 py-10 text-center text-gray-400 text-sm italic">Select a department above to load staff</div>
            
            <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <p class="text-xs text-gray-500 dark:text-gray-400">Staff without a tick will be <strong>automatically marked absent</strong>. Already-existing records for this date are skipped.</p>
              <div class="flex gap-3">
                <button @click="closeBatchModal" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">Cancel</button>
                <button @click="submitBatchAttendance" :disabled="batchStaff.length === 0 || batchSubmitting" class="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#65a348] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <span v-if="batchSubmitting">Saving…</span>
                  <span v-else>Submit Batch ({{ batchStaff.length }} staff)</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. Attendance Details Modal -->
        <div v-if="selectedAttendance" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Attendance Details - {{ formatDate(selectedAttendance.date) }}</h3>
              <button @click="selectedAttendance = null" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div class="px-6 py-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-4">
                  <div><p class="text-sm text-gray-500 dark:text-gray-400">Employee</p><p class="font-medium text-gray-900 dark:text-white">{{ selectedAttendance.employeeName }}</p></div>
                  <div><p class="text-sm text-gray-500 dark:text-gray-400">Date</p><p class="font-medium text-gray-900 dark:text-white">{{ formatDate(selectedAttendance.date) }}</p></div>
                  <div><p class="text-sm text-gray-500 dark:text-gray-400">Status</p>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1" :class="getStatusClass(selectedAttendance.status)">
                      <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="getStatusDot(selectedAttendance.status)"></span>
                      {{ selectedAttendance.status }}
                    </span>
                  </div>
                </div>
                <div class="space-y-4">
                  <div><p class="text-sm text-gray-500 dark:text-gray-400">Check-In Time</p><p class="font-medium text-gray-900 dark:text-white">{{ selectedAttendance.checkIn || 'N/A' }}</p></div>
                  <div><p class="text-sm text-gray-500 dark:text-gray-400">Check-Out Time</p><p class="font-medium text-gray-900 dark:text-white">{{ selectedAttendance.checkOut || 'N/A' }}</p></div>
                </div>
              </div>
              <div v-if="selectedAttendance.notes" class="mt-6">
                <p class="text-sm text-gray-500 dark:text-gray-400">Notes</p>
                <p class="mt-1 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-gray-900 dark:text-white text-sm">{{ selectedAttendance.notes }}</p>
              </div>
            </div>
            <div v-if="isManager" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <button @click="correctRecord(selectedAttendance)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#65a348] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#76ba51]">Correct Record</button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import * as XLSX from 'xlsx';
import api from "@/services/api";
import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';

// ==================== Reactive State ====================
const isSidebarOpen = ref(false);
const isDarkMode = ref(false);
const currentPageTitle = ref('Attendance Management');
const isManager = ref(true);
const showDetailedView = ref(false);

const employees = ref([]);
const departments = ref([]);
const positions = ref([]);
const classes = ref([]);
const selectedEmployee = ref(null);

const allAttendanceRecords = ref([]);
const dailyAttendanceLoading = ref(false);
const attendanceRecords = ref([]);          
const employeeAttendanceLoading = ref(false);

const showNewAttendanceModal = ref(false);
const selectedAttendance = ref(null);

const employeeSearch = ref('');
const searchResults = ref([]);
const statuses = ref([]);

const newAttendance = ref({
  id: null,
  date: new Date().toISOString().split('T')[0],
  status: 'present',
  status_id: null,
  checkIn: '',
  checkOut: '',
  notes: '',
});

const totalRecords = ref(0);
const presentToday = ref(0);
const absentToday = ref(0);
const lateToday = ref(0);

const dailyFilterFromDate = ref('');
const dailyFilterToDate = ref('');
const dailyFilterStatus = ref('all');
const dailyFilterDepartment = ref('all');
const dailyFilterPosition = ref('all');
const dailyFilterClass = ref('all');

const filterFromDate = ref('');
const filterToDate = ref('');
const filterStatus = ref('all');
const attendanceSummary = ref({ present: 0, absent: 0, late: 0 });

const showBatchModal = ref(false);
const batchDepartmentCode = ref('');
const batchDate = ref(new Date().toISOString().split('T')[0]);
const batchDefaultCheckIn = ref('07:00');
const batchDefaultCheckOut = ref('15:00');
const batchStaff = ref([]);
const batchStaffSearch = ref('');
const batchLoading = ref(false);
const batchSubmitting = ref(false);

// ==================== Computed Properties ====================
const filteredDailyAttendance = computed(() => {
  let filtered = [...allAttendanceRecords.value];
  if (dailyFilterFromDate.value) filtered = filtered.filter(r => r.date >= dailyFilterFromDate.value);
  if (dailyFilterToDate.value) filtered = filtered.filter(r => r.date <= dailyFilterToDate.value);
  if (dailyFilterStatus.value !== 'all') filtered = filtered.filter(r => r.status === dailyFilterStatus.value);
  if (dailyFilterDepartment.value !== 'all') filtered = filtered.filter(r => r.department === dailyFilterDepartment.value);
  if (dailyFilterPosition.value !== 'all') filtered = filtered.filter(r => r.position === dailyFilterPosition.value);
  if (dailyFilterClass.value !== 'all') filtered = filtered.filter(r => r.class === dailyFilterClass.value);
  return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
});

const filteredBatchStaff = computed(() => {
  if (!batchStaffSearch.value) return batchStaff.value;
  const term = batchStaffSearch.value.toLowerCase();
  return batchStaff.value.filter(s => s.name.toLowerCase().includes(term) || s.id.toLowerCase().includes(term));
});

const batchPresentCount = computed(() => batchStaff.value.filter(s => s.isPresent).length);
const allBatchChecked = computed(() => batchStaff.value.length > 0 && batchPresentCount.value === batchStaff.value.length);
const someBatchChecked = computed(() => batchPresentCount.value > 0 && batchPresentCount.value < batchStaff.value.length);

const presentStatuses = computed(() => statuses.value.filter(s => !s.name.toLowerCase().includes('absent')));
const absentStatusId = computed(() => {
  const found = statuses.value.find(s => s.name.toLowerCase().includes('absent'));
  return found?.id ?? null;
});

const departmentObjects = computed(() => {
  const map = new Map();
  employees.value.forEach(emp => {
    if (emp.department_code && emp.department && emp.department !== 'N/A') {
      map.set(emp.department_code, emp.department);
    }
  });
  return Array.from(map.entries()).map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
});

// ==================== Fetch Data on Mount ====================
onMounted(async () => {
  try {
    const res = await api.get('get_hired_applicants.php');
    if (res.data.success) {
      employees.value = res.data.data.map(emp => ({
        id: emp.employee_code || `APP-${emp.applicant_id}`,
        applicant_id: emp.applicant_id,
        employee_id: emp.employee_id,
        employee_code: emp.employee_code,
        name: emp.full_name || `Applicant #${emp.applicant_id}`,
        position: emp.position?.position_title || 'N/A',
        department: emp.department?.department_name || 'N/A',
        department_code: emp.department?.department_code || null,
        section: emp.section?.section_name?.toLowerCase() || 'non-teaching',
        class: emp.class?.class_name || 'N/A',
      }));
      
      departments.value = Array.from(new Set(employees.value.map(e => e.department).filter(d => d && d !== 'N/A'))).sort();
      positions.value = Array.from(new Set(employees.value.map(e => e.position).filter(p => p && p !== 'N/A'))).sort();
      classes.value = Array.from(new Set(employees.value.map(e => e.class).filter(c => c && c !== 'N/A'))).sort();
    }
  } catch (err) { console.error('Error fetching employees:', err); }

  try {
    const response = await api.get("AttendanceStatus.php");
    statuses.value = response.data.map(status => ({ id: status.id, name: status.name, code: status.code, color: status.color, description: status.description }));
  } catch (error) { console.error("Error fetching statuses:", error); }
  
  fetchDailyAttendance();
});

// ==================== Fetch Attendance ====================
const fetchDailyAttendance = async () => {
  dailyAttendanceLoading.value = true;
  try {
    const params = new URLSearchParams();
    if (dailyFilterFromDate.value) params.append('from_date', dailyFilterFromDate.value);
    if (dailyFilterToDate.value) params.append('to_date', dailyFilterToDate.value);
    if (dailyFilterStatus.value !== 'all') params.append('status', dailyFilterStatus.value);
    if (dailyFilterDepartment.value !== 'all') params.append('department', dailyFilterDepartment.value);
    if (dailyFilterPosition.value !== 'all') params.append('position', dailyFilterPosition.value);
    if (dailyFilterClass.value !== 'all') params.append('class', dailyFilterClass.value);
    
    const response = await api.get(`get_all_attendances.php?${params.toString()}`);
    if (response.data.status === "success") {
      allAttendanceRecords.value = response.data.data.map(record => ({
        id: record.id,
        applicant_id: record.applicant_id,
        employeeName: record.full_name || `Applicant #${record.applicant_id}`,
        employeeId: record.employee_code || `APP-${record.applicant_id}`,
        department: record.department?.department_name || 'N/A',
        position: record.position?.position_title || 'N/A',
        class: record.class?.class_name || 'N/A',
        date: record.attendance_date,
        status: (record.status_name || 'unknown').toLowerCase().trim(),
        statusLabel: record.status_name || 'Unknown',
        color: record.color || null,
        checkIn: record.check_in ? record.check_in.substring(11, 16) : null,
        checkOut: record.check_out ? record.check_out.substring(11, 16) : null,
        workHours: record.work_hours,
        overtimeHours: record.overtime_hours,
        lateMinutes: record.late_minutes,
        notes: record.notes,
        attendance_type: record.attendance_type,
      }));

      const today = new Date().toISOString().split('T')[0];
      const todayRecords = allAttendanceRecords.value.filter(r => r.date === today);
      totalRecords.value = allAttendanceRecords.value.length;
      presentToday.value = todayRecords.filter(r => r.status === 'present').length;
      absentToday.value = todayRecords.filter(r => r.status === 'absent').length;
      lateToday.value = todayRecords.filter(r => r.status === 'late').length;
    }
  } catch (error) {
    console.error("Error fetching daily attendance:", error);
    allAttendanceRecords.value = [];
  } finally { dailyAttendanceLoading.value = false; }
};

const fetchAttendance = async () => {
  if (!selectedEmployee.value?.applicant_id) return;
  employeeAttendanceLoading.value = true;
  try {
    const params = new URLSearchParams({ applicant_id: selectedEmployee.value.applicant_id });
    if (filterFromDate.value) params.append('from_date', filterFromDate.value);
    if (filterToDate.value) params.append('to_date', filterToDate.value);
    if (filterStatus.value && filterStatus.value !== 'all') params.append('status_name', filterStatus.value.toLowerCase());

    const response = await api.get(`get_attendance.php?${params.toString()}`);
    if (response.data.status === "success" || response.data.success) {
      const rawData = response.data.data || response.data.records || [];
      attendanceRecords.value = rawData.map(record => ({
        id: record.id,
        applicant_id: record.applicant_id,
        employeeName: record.full_name?.trim() || selectedEmployee.value.name,
        employeeId: record.employee_code || selectedEmployee.value.id,
        date: record.attendance_date,
        status: (record.status_name || record.status || 'unknown').toLowerCase().trim(),
        status_code: record.status_code || null,
        color: record.color,
        checkIn: record.check_in ? record.check_in.substring(11, 16) : null,
        checkOut: record.check_out ? record.check_out.substring(11, 16) : null,
        workHours: record.work_hours,
        overtimeHours: record.overtime_hours,
        lateMinutes: record.late_minutes,
        notes: record.notes,
        attendance_type: record.attendance_type,
      }));
      updateAttendanceSummary();
    }
  } catch (error) {
    console.error("Error fetching attendance:", error);
    attendanceRecords.value = [];
    attendanceSummary.value = { present: 0, absent: 0, late: 0 };
  } finally { employeeAttendanceLoading.value = false; }
};

const updateAttendanceSummary = () => {
  const summary = { present: 0, absent: 0, late: 0 };
  attendanceRecords.value.forEach(record => {
    if (record.status === 'present') summary.present++;
    else if (record.status === 'absent') summary.absent++;
    else if (record.status === 'late') summary.late++;
  });
  attendanceSummary.value = summary;
};

// ==================== Employee Search & Selection ====================
const searchEmployees = () => {
  if (employeeSearch.value.length < 2) { searchResults.value = []; return; }
  const term = employeeSearch.value.toLowerCase();
  searchResults.value = employees.value.filter(emp => emp.name.toLowerCase().includes(term) || emp.id.toLowerCase().includes(term));
};

const selectEmployee = (employee) => {
  selectedEmployee.value = employee;
  employeeSearch.value = employee.name;
  searchResults.value = [];
  attendanceSummary.value = { present: 0, absent: 0, late: 0 };
  fetchAttendance();
};

const clearSelectedEmployee = () => {
  selectedEmployee.value = null;
  employeeSearch.value = '';
  attendanceRecords.value = [];
};

// ==================== Filter Reset ====================
const resetDailyFilters = () => {
  dailyFilterFromDate.value = '';
  dailyFilterToDate.value = '';
  dailyFilterStatus.value = 'all';
  dailyFilterDepartment.value = 'all';
  dailyFilterPosition.value = 'all';
  dailyFilterClass.value = 'all';
  fetchDailyAttendance();
};

const resetFilters = () => {
  filterFromDate.value = '';
  filterToDate.value = '';
  filterStatus.value = 'all';
  fetchAttendance();
};

// ==================== Excel Export ====================
const exportToExcel = () => {
  if (filteredDailyAttendance.value.length === 0) { alert('No data to export'); return; }
  const exportData = filteredDailyAttendance.value.map(record => ({
    'Employee Name': record.employeeName,
    'Staff ID': record.employeeId,
    'Department': record.department,
    'Position': record.position,
    'Class': record.class,
    'Date': formatDate(record.date),
    'Status': record.status,
    'Check-In': record.checkIn || 'N/A',
    'Check-Out': record.checkOut || 'N/A',
    'Work Hours': record.workHours || 'N/A',
    'Overtime Hours': record.overtimeHours || '0',
    'Late Minutes': record.lateMinutes || '0',
    'Notes': record.notes || 'N/A',
  }));

  const ws = XLSX.utils.json_to_sheet(exportData);
  ws['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 30 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Records');
  XLSX.writeFile(wb, `Attendance_Records_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// ==================== New Attendance Modal Logic ====================
const requiresCheckIn = (statusName) => {
  if (!statusName) return false;
  const name = statusName.toLowerCase();
  return name.includes('present') || name.includes('late') || name.includes('on time');
};

const openNewAttendanceModal = () => {
  if (!selectedEmployee.value) { alert('Please select an employee first'); return; }
  const defaultStatus = statuses.value.find(s => s.name.toLowerCase() === 'present') || statuses.value[0];
  newAttendance.value = { id: null, date: new Date().toISOString().split('T')[0], status: defaultStatus?.name || 'present', status_id: defaultStatus?.id || null, checkIn: '', checkOut: '', notes: '' };
  showNewAttendanceModal.value = true;
};

const updateStatusId = () => {
  const found = statuses.value.find(s => s.name.toLowerCase() === newAttendance.value.status.toLowerCase());
  newAttendance.value.status_id = found ? found.id : null;
};

const submitAttendance = async () => {
  if (!selectedEmployee.value?.applicant_id) return alert('Please select an employee first');
  if (!newAttendance.value.date) return alert('Please select a date');
  if ((newAttendance.value.status === 'present' || newAttendance.value.status === 'late') && !newAttendance.value.checkIn) return alert('Please provide a check-in time');

  let workHours = null, overtimeHours = 0, lateMinutes = 0, earlyLeaveMinutes = 0;
  const checkIn = newAttendance.value.checkIn;
  const checkOut = newAttendance.value.checkOut;

  if (checkIn && checkOut) {
    const checkInTime = new Date(`2000-01-01 ${checkIn}`);
    const checkOutTime = new Date(`2000-01-01 ${checkOut}`);
    const diffMs = checkOutTime - checkInTime;
    workHours = parseFloat((diffMs / (1000 * 60 * 60)).toFixed(2));
    if (workHours > 8.0) overtimeHours = parseFloat((workHours - 8.0).toFixed(2));
  }

  let expectedStartTime = "08:00";
  if (selectedEmployee.value.position) {
    const title = selectedEmployee.value.position.toLowerCase();
    if (title.includes("teacher") || title.includes("teach")) expectedStartTime = "07:00";
  }

  if (checkIn) {
    const checkInTime = new Date(`2000-01-01 ${checkIn}`);
    const expectedStart = new Date(`2000-01-01 ${expectedStartTime}`);
    if (checkInTime > expectedStart) {
      const diffMs = checkInTime - expectedStart;
      lateMinutes = Math.floor(diffMs / (1000 * 60));
    }
  }

  const payload = {
    applicant_id: selectedEmployee.value.applicant_id,
    attendance_date: newAttendance.value.date,
    check_in: checkIn || null,
    check_out: checkOut || null,
    work_hours: workHours,
    overtime_hours: overtimeHours,
    late_minutes: lateMinutes,
    early_leave_minutes: earlyLeaveMinutes,
    status_id: newAttendance.value.status_id,
    attendance_type: 'manual',
    updated_by: 1,
    notes: newAttendance.value.notes || null,
  };

  try {
    let response;
    if (newAttendance.value.id) {
      payload.id = newAttendance.value.id;
      response = await api.put('save_attendance.php', payload);
    } else {
      response = await api.post('save_attendance.php', payload);
    }

    if (response.data.status === "success") {
      alert(newAttendance.value.id ? 'Attendance updated successfully!' : 'Attendance saved successfully!');
      const newRecord = {
        id: response.data.id || newAttendance.value.id || Date.now(),
        employeeName: selectedEmployee.value.name,
        employeeId: selectedEmployee.value.id,
        department: selectedEmployee.value.department,
        position: selectedEmployee.value.position,
        class: selectedEmployee.value.class,
        date: newAttendance.value.date,
        status: newAttendance.value.status,
        checkIn: checkIn,
        checkOut: checkOut,
        notes: newAttendance.value.notes,
      };

      if (newAttendance.value.id) {
        const idx1 = attendanceRecords.value.findIndex(r => r.id === newAttendance.value.id);
        if (idx1 !== -1) attendanceRecords.value[idx1] = newRecord;
        const idx2 = allAttendanceRecords.value.findIndex(r => r.id === newAttendance.value.id);
        if (idx2 !== -1) allAttendanceRecords.value[idx2] = newRecord;
      } else {
        attendanceRecords.value.unshift(newRecord);
        allAttendanceRecords.value.unshift(newRecord);
      }

      showNewAttendanceModal.value = false;
      newAttendance.value = { id: null, date: new Date().toISOString().split('T')[0], status: 'present', status_id: null, checkIn: '', checkOut: '', notes: '' };
      fetchDailyAttendance();
      if (selectedEmployee.value) fetchAttendance();
    } else {
      alert(response.data.message || 'Failed to save attendance');
    }
  } catch (error) {
    console.error('Attendance error:', error);
    alert('Error saving attendance. Please try again.');
  }
};

// ==================== Batch Attendance Logic ====================
const toggleAllBatch = (e) => {
  const checked = e.target.checked;
  batchStaff.value.forEach(s => {
    s.isPresent = checked;
    if (checked && !s.checkIn) s.checkIn = batchDefaultCheckIn.value;
  });
};

const onBatchCheckChange = (staff) => {
  if (staff.isPresent && !staff.checkIn) staff.checkIn = batchDefaultCheckIn.value;
};

const openBatchModal = () => {
  batchDepartmentCode.value = '';
  batchDate.value = new Date().toISOString().split('T')[0];
  batchDefaultCheckIn.value = '07:00';
  batchDefaultCheckOut.value = '15:00';
  batchStaff.value = [];
  batchStaffSearch.value = '';
  showBatchModal.value = true;
};

const closeBatchModal = () => { showBatchModal.value = false; };

const loadBatchStaff = () => {
  if (!batchDepartmentCode.value) { batchStaff.value = []; return; }
  batchLoading.value = true;
  const defaultPresentStatusId = presentStatuses.value[0]?.id ?? null;
  batchStaff.value = employees.value
    .filter(emp => emp.department_code === batchDepartmentCode.value)
    .map(emp => ({
      applicant_id: emp.applicant_id,
      name: emp.name,
      id: emp.id,
      position: emp.position,
      isPresent: false,
      statusId: defaultPresentStatusId,
      checkIn: '',
    }));
  batchLoading.value = false;
};

const submitBatchAttendance = async () => {
  if (!batchDepartmentCode.value) return alert('Please select a department');
  if (!batchDate.value) return alert('Please select a date');
  if (!absentStatusId.value) return alert('Could not find an "Absent" status. Check your statuses setup.');
  if (batchStaff.value.length === 0) return alert('No staff loaded');

  batchSubmitting.value = true;
  const presentRecords = batchStaff.value.filter(s => s.isPresent).map(s => {
    const checkIn = s.checkIn || batchDefaultCheckIn.value || null;
    const checkOut = batchDefaultCheckOut.value || null;
    let workHours = null, overtimeHours = 0, lateMinutes = 0;

    if (checkIn && checkOut) {
      const inTime = new Date(`2000-01-01 ${checkIn}`);
      const outTime = new Date(`2000-01-01 ${checkOut}`);
      workHours = parseFloat(((outTime - inTime) / 3_600_000).toFixed(2));
      if (workHours > 8) overtimeHours = parseFloat((workHours - 8).toFixed(2));
    }
    if (checkIn) {
      const inTime = new Date(`2000-01-01 ${checkIn}`);
      const expected = new Date('2000-01-01 08:00');
      if (inTime > expected) lateMinutes = Math.floor((inTime - expected) / 60_000);
    }

    return { applicant_id: s.applicant_id, status_id: s.statusId, check_in: checkIn, check_out: checkOut, work_hours: workHours, overtime_hours: overtimeHours, late_minutes: lateMinutes, notes: null };
  });

  const payload = { attendance_date: batchDate.value, department_code: batchDepartmentCode.value, attendance_type: 'manual', updated_by: 1, absent_status_id: absentStatusId.value, records: presentRecords };

  try {
    const response = await api.post('save_batch_attendance.php', payload);
    const res = response.data;
    if (res.status === 'success') {
      const msg = [`✅ Batch saved successfully!`, `• Inserted: ${res.inserted}`, `• Auto-marked absent: ${res.absent_marked}`, res.skipped > 0 ? `• Skipped (already existed): ${res.skipped}` : null, res.errors?.length > 0 ? `• Errors: ${res.errors.length}` : null].filter(Boolean).join('\n');
      alert(msg);
      closeBatchModal();
      fetchDailyAttendance();
    } else {
      alert(res.message || 'Batch save failed');
    }
  } catch (err) {
    console.error('Batch attendance error:', err);
    alert('Error submitting batch attendance. Please try again.');
  } finally { batchSubmitting.value = false; }
};

// ==================== Helper Functions ====================
const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try { return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } 
  catch { return 'N/A'; }
};

const getStatusClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'present') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  if (s === 'absent') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  if (s === 'late') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const getStatusDot = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'present') return 'bg-green-500';
  if (s === 'absent') return 'bg-red-500';
  if (s === 'late') return 'bg-yellow-500';
  return 'bg-gray-500';
};

const viewAttendanceDetails = (record) => { selectedAttendance.value = record; };

const correctRecord = (record) => {
  if (!selectedEmployee.value && record.employeeId) {
    const emp = employees.value.find(e => e.id === record.employeeId);
    if (emp) selectedEmployee.value = emp;
  }
  newAttendance.value = { id: record.id, date: record.date, status: record.status, status_id: null, checkIn: record.checkIn || '', checkOut: record.checkOut || '', notes: record.notes || '' };
  const found = statuses.value.find(s => s.name.toLowerCase() === record.status.toLowerCase());
  if (found) newAttendance.value.status_id = found.id;
  showNewAttendanceModal.value = true;
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) { document.documentElement.classList.add('dark'); localStorage.theme = 'dark'; } 
  else { document.documentElement.classList.remove('dark'); localStorage.theme = 'light'; }
};
</script>